import { useRef, useEffect, useState, type ComponentType, type SVGProps } from 'react';
import type { KeyboardEvent, ChangeEvent } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { refineOrbiPrompt } from '../../lib/orbiApi';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ArrowUpIcon,
  BeakerIcon,
  BookOpenIcon,
  FolderIcon,
  LockClosedIcon,
  MicrophoneIcon,
  PaperClipIcon,
  PlusIcon,
  SparklesIcon,
  StopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
// PaperClipIcon is still imported for the attachment chips above the
// input — the right-side paperclip button is gone (the plus on the
// left now opens the file picker).

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

export type ChatComposerMode = 'workflow' | 'research' | 'portfolio';

const modes: { id: ChatComposerMode; label: string; icon: IconComponent }[] = [
  { id: 'workflow', label: 'Workflow', icon: BeakerIcon },
  { id: 'research', label: 'Research', icon: BookOpenIcon },
  { id: 'portfolio', label: 'Portfolio', icon: FolderIcon },
];

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  mode?: ChatComposerMode;
  onModeChange?: (mode: ChatComposerMode) => void;
  disabled?: boolean;
  placeholder?: string;
  disclaimer?: string;
  showMic?: boolean;
  onMic?: () => void;
  autoFocus?: boolean;
  className?: string;
  /**
   * Show the "Refine prompt" sparkles button. Defaults to true. Set false
   * on surfaces where refinement doesn't apply (e.g. a plain search input).
   */
  showRefine?: boolean;
  /**
   * Show the paperclip file-attach button. Attached files are
   * returned on submit via onAttachmentsChange. Defaults to true.
   */
  showAttach?: boolean;
  /** Current attachment list (controlled). */
  attachments?: File[];
  /** Called when the attachment list changes. */
  onAttachmentsChange?: (files: File[]) => void;
  /**
   * When set, the composer renders a locked state: send button becomes
   * an "Upgrade" pill and the field is disabled. Free-tier signal from
   * the caller — the composer stays presentational.
   */
  locked?: boolean;
  /** Called when the user clicks Upgrade on the locked state. */
  onUpgrade?: () => void;
  /** Optional inline copy shown next to the lock. */
  lockedMessage?: string;
}

// Browser SpeechRecognition — vendor-prefixed on WebKit.
type SR = typeof globalThis extends { SpeechRecognition: infer T } ? T : any;
function getSpeechRecognitionCtor(): SR | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SR;
    webkitSpeechRecognition?: SR;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export default function ChatComposer({
  value,
  onChange,
  onSubmit,
  mode,
  onModeChange,
  disabled = false,
  placeholder = 'Write a message...',
  disclaimer = 'Orbi can make mistakes. Check important Info.',
  showMic = true,
  onMic,
  autoFocus = false,
  className,
  showRefine = true,
  locked = false,
  onUpgrade,
  lockedMessage = 'Orbi is a Pro feature — upgrade to send.',
  showAttach = true,
  attachments,
  onAttachmentsChange,
}: ChatComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uncontrolledFiles, setUncontrolledFiles] = useState<File[]>([]);
  const files = attachments ?? uncontrolledFiles;
  const setFiles = (next: File[]) => {
    if (onAttachmentsChange) onAttachmentsChange(next);
    else setUncontrolledFiles(next);
  };
  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const next = [...files, ...Array.from(list)].slice(0, 6);
    setFiles(next);
  };
  const removeFile = (i: number) => setFiles(files.filter((_, j) => j !== i));
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const [micSupported] = useState(() => !!getSpeechRecognitionCtor());
  // Text captured before recording starts, so we append new transcript instead of replacing it.
  const preRecordValueRef = useRef('');

  // Prompt refiner state. `previousValue` holds the pre-refine text so
  // the user can undo the rewrite with a single click; clearing it on
  // manual edits happens in handleTextareaChange.
  const [refining, setRefining] = useState(false);
  const [previousValue, setPreviousValue] = useState<string | null>(null);

  const handleRefine = async () => {
    const source = value.trim();
    if (!source || refining || disabled) return;
    setRefining(true);
    try {
      const improved = await refineOrbiPrompt(source);
      setPreviousValue(source);
      onChange(improved);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Refiner failed';
      toast.error(message);
    } finally {
      setRefining(false);
    }
  };

  const handleUndoRefine = () => {
    if (previousValue === null) return;
    onChange(previousValue);
    setPreviousValue(null);
  };

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 240) + 'px';
  }, [value]);

  const stopListening = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }
    setListening(false);
  };

  useEffect(() => () => { stopListening(); }, []);

  const startListening = () => {
    if (onMic) { onMic(); return; }
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    if (listening) { stopListening(); return; }

    // eslint-disable-next-line new-cap
    const rec = new (Ctor as any)();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';
    preRecordValueRef.current = value;

    rec.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += chunk;
        else interimText += chunk;
      }
      const base = preRecordValueRef.current;
      const combined = `${base}${base && (finalText || interimText) ? ' ' : ''}${finalText}${interimText}`.trim();
      onChange(combined);
      if (finalText) preRecordValueRef.current = `${base}${base ? ' ' : ''}${finalText}`.trim();
    };
    rec.onerror = () => stopListening();
    rec.onend = () => setListening(false);

    try {
      rec.start();
      recognitionRef.current = rec;
      setListening(true);
    } catch {
      stopListening();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (listening) stopListening();
      if (value.trim() && !disabled) onSubmit();
    }
  };

  const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    // Manual typing wins — snapshot the current value so any subsequent
    // voice input appends from this point.
    if (listening) preRecordValueRef.current = e.target.value;
    // A manual edit means the "undo refine" affordance is no longer meaningful.
    if (previousValue !== null) setPreviousValue(null);
    onChange(e.target.value);
  };

  const handleSend = () => {
    if (listening) stopListening();
    if (value.trim() && !disabled) onSubmit();
  };

  return (
    <div className={cn('flex w-full flex-col items-center gap-2 font-display', className)}>
      <div className={cn(
        'flex w-full flex-col gap-2 rounded-2xl border border-border bg-card p-4 transition-colors',
        listening ? 'border-primary ring-2 ring-primary/20' : 'focus-within:border-primary',
      )}>
        <div className="flex items-center gap-2">
          {modes.map((m) => {
            const active = mode === m.id;
            const ModeIcon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onModeChange?.(m.id)}
                className={cn(
                  'flex items-center gap-1 rounded-full px-2 py-1 text-sm transition-colors',
                  active
                    ? 'bg-muted text-foreground'
                    : 'text-foreground/80 hover:text-foreground',
                )}
                aria-pressed={active}
              >
                <ModeIcon className="h-5 w-5" />
                <span>{m.label}</span>
              </button>
            );
          })}
        </div>

        {/* Attachment chips row — appears above the input when files
            are attached. Each chip has a tiny × to remove it. */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pb-1">
            {files.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 rounded-full border border-border bg-muted/60 pl-2 pr-1 py-1 text-xs text-foreground max-w-[200px]"
              >
                <PaperClipIcon className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  aria-label={`Remove ${f.name}`}
                  className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted-foreground/20 hover:text-foreground"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end justify-between gap-2">
          <div className="flex flex-1 items-start gap-2">
            {/* Plus — opens the file picker so the icon in its
                established position does the attaching. The hidden
                <input> element lives in the right-side cluster. */}
            {showAttach ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                aria-label="Attach image or file"
                title="Attach image or file"
                className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
              >
                <PlusIcon className="h-6 w-6" />
              </button>
            ) : (
              <PlusIcon className="mt-1.5 h-6 w-6 flex-shrink-0 text-muted-foreground" />
            )}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={listening ? 'Listening…' : placeholder}
              disabled={disabled}
              rows={1}
              className="flex-1 resize-none border-0 bg-transparent py-1 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none disabled:opacity-50"
              aria-label="Message"
            />
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            {showRefine && value.trim() && !listening && previousValue === null && (
              <button
                type="button"
                onClick={handleRefine}
                disabled={disabled || refining}
                aria-label="Refine prompt"
                title="Refine prompt — make it more specific and effective"
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50',
                  refining && 'border-primary text-primary',
                )}
              >
                {refining ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <SparklesIcon className="h-4 w-4" />
                )}
              </button>
            )}
            {showRefine && previousValue !== null && !listening && (
              <button
                type="button"
                onClick={handleUndoRefine}
                aria-label="Undo prompt refinement"
                title="Undo refinement — bring back your original prompt"
                className="flex h-8 items-center gap-1 rounded-full border border-primary/40 bg-primary/5 pl-2 pr-3 text-xs font-medium text-primary transition-opacity hover:opacity-80"
              >
                <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                <span>Undo refine</span>
              </button>
            )}
            {/* Hidden <input type="file"> — triggered by the plus button
                on the left of the composer, not a paperclip here (the
                plus has had that spot since launch and users asked for
                it to double as the attach control). */}
            {showAttach && (
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
            )}
            {/* Mic — visible regardless of whether the field has text
                (previous `!value` guard was the "voice note broken"
                bug — once a user typed anything, mic disappeared). */}
            {showMic && micSupported && !listening && (
              <button
                type="button"
                onClick={startListening}
                disabled={disabled}
                aria-label="Start voice input"
                title="Hold to dictate"
                className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary transition-colors disabled:opacity-50"
              >
                <MicrophoneIcon className="h-4 w-4" />
              </button>
            )}
            {listening && (
              <button
                type="button"
                onClick={stopListening}
                aria-label="Stop voice input"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm animate-pulse"
              >
                <StopIcon className="h-4 w-4" />
              </button>
            )}
            {locked ? (
              <button
                type="button"
                onClick={onUpgrade}
                aria-label="Upgrade plan to send"
                className="flex h-8 items-center gap-1.5 rounded-full bg-primary pl-3 pr-3.5 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90"
              >
                <LockClosedIcon className="h-3.5 w-3.5" />
                <span>Upgrade to run</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={disabled || !value.trim() || refining}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-muted-foreground/50"
                aria-label="Send message"
              >
                <ArrowUpIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {locked ? (
        <p className="text-center text-xs text-muted-foreground/80">{lockedMessage}</p>
      ) : disclaimer ? (
        <p className="text-center text-xs text-muted-foreground/70">{disclaimer}</p>
      ) : null}
    </div>
  );
}
